import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {
    Flow,
    FlowBlock,
    FlowProperty,
    FlowTick,
    FlowWire,
    INPUT_COMPONENT_NAME,
    TopologyState
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {ResolvedCloudArtifact} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {IFunctionArgSpec, IFunctionSpec} from '@mintdata/mint-kit/api/decorators/Function';
import {SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';

export abstract class FlowStateUtils
{
    public static generateTopologyState(blocks:FlowBlock[]):TopologyState
    {
        const topologyState = new TopologyState();
        topologyState.components = FlowStateUtils._generateComponentsJson(blocks);
        topologyState.links = FlowStateUtils._generateLinksJson(blocks);

        return topologyState;
    }

    public static getArtifactsForSaveRequest():{type:string, descriptor:string}[]
    {
        const {artifacts} = commonControllers;

        return artifacts.getCloudArtifacts().map((artifact:ResolvedCloudArtifact) =>
        {
            return {
                type: 'MAVEN',
                descriptor: artifact.getMavenDescriptor()
            };
        });
    }

    public static generateFuncSpec(flow:Flow):IFunctionSpec
    {
        const funcArguments = flow.orderedInputNames;

        const title = `${flow.name}(${funcArguments.join(', ')})`;
        const exampleProperty = flow.output.properties.find(({name}) => name === 'example');
        const descriptionProperty = flow.output.properties.find(({name}) => name === 'description');

        return {
            title,
            tip: descriptionProperty ? descriptionProperty.value : '',
            example: exampleProperty ? exampleProperty.value : '',
            alias: flow.name,
            returns: {
                type: Type.Frame,
                title: 'Frame',
                tip: 'Returns a frame'
            } as IFunctionArgSpec
        };
    }

    public static generateFuncArgSpecs(flowBlocks:FlowBlock[], withSheetAPIAsFirstArg:boolean = true):IFunctionArgSpec[]
    {
        const argSpecs:IFunctionArgSpec[] = flowBlocks.map((flowBlock) =>
        {
            const typeProperty = flowBlock.properties.find(({name}) => name === 'type');
            const descriptionProperty = flowBlock.properties.find(({name}) => name === 'description');

            const type = typeProperty ? Type.fromTypeName(typeProperty.value) : Type.String;
            const tip = descriptionProperty ? descriptionProperty.value : '';

            return {
                title: flowBlock.instanceName,
                type,
                tip
            };
        });

        if( withSheetAPIAsFirstArg )
        {
            argSpecs.unshift({
                title: 'api',
                type: SheetType.SheetAPI,
                tip: 'api'
            });
        }

        return argSpecs;
    }

    private static _generateComponentsJson(blocks:FlowBlock[]):any
    {
        return blocks.map((block:FlowBlock) =>
        {
            return FlowStateUtils._toComponentJSON(block);
        });
    }

    private static _toComponentJSON(block:FlowBlock):any
    {
        let component = block.component;

        const componentJson:any =
            {
                'type': 'component',
                'clazz': component.javaClassFQN,
                'definitionIdentifier': component.javaClassFQN,
                'id': block.id,
                'instanceName': block.instanceName,
                'parallelism': 1,
                'visualSettings': null
            };

        componentJson.properties = {};
        componentJson.ticks = {};

        block.properties.map((p:FlowProperty) =>
        {
            componentJson.properties[p.name] = p.value;
        });

        block.ticks.map((t:FlowTick) =>
        {
            componentJson.ticks[t.definition.name] =
                {
                    interval: t.interval,
                    unit: t.unit
                };
        });

        return componentJson;
    }

    private static _generateLinksJson(blocks:FlowBlock[]):any[]
    {
        const linksJson:any[] = [];
        const alreadySeenWireIds = {};

        for( let i = 0; i < blocks.length; i++ )
        {
            const block = blocks[i];
            FlowStateUtils._generateLinksForBlock(block, alreadySeenWireIds, linksJson);
        }

        FlowStateUtils._attachInputBlockLinksToEntryBlock(blocks, linksJson);

        return linksJson;
    }

    private static _attachInputBlockLinksToEntryBlock(blocks:FlowBlock[], allLinksJson:any[])
    {
        const flowEntryBlock = blocks.find(({isEntryBlock}) => isEntryBlock);
        const inputBlockIds = blocks
            .filter(({component}) => component.name === INPUT_COMPONENT_NAME)
            .map(({id}) => id);

        for( let i = 0; i < allLinksJson.length; i++ )
        {
            const linkJson = allLinksJson[i];

            if( inputBlockIds.indexOf(linkJson.out) !== -1 )
            {
                linkJson.out = flowEntryBlock.id;
                linkJson.outPort = flowEntryBlock.outputPorts[0].name;
            }
        }
    }

    private static _generateLinksForBlock(block:FlowBlock, alreadySeenWireIds:any, allLinksJson:any[]):void
    {
        const inWires = block.inWires;
        const outWires = block.outWires;

        this._generateLinksForWires(block, inWires, alreadySeenWireIds, allLinksJson, true);
        this._generateLinksForWires(block, outWires, alreadySeenWireIds, allLinksJson, false);
    }

    private static _generateLinksForWires(block:FlowBlock,
                                          wires:FlowWire[],
                                          alreadySeenWireIds:any,
                                          allLinksJson:any[],
                                          isInputWires)
    {
        for( let i = 0; i < wires.length; i++ )
        {
            const wire = wires[i];

            if( !alreadySeenWireIds[wire.id] )
            {
                alreadySeenWireIds[wire.id] = true;

                const linkJson = this._toLinkJson(wire, block, isInputWires);
                allLinksJson.push(linkJson);
            }
        }
    }

    private static _toLinkJson(wire:FlowWire, block:FlowBlock, isInputWire:boolean):any
    {
        const sourceBlock = wire.source.block;
        const sourcePortName = wire.source.port.name;
        const targetBlock = wire.target.block;
        const targetPortName = wire.target.port.name;

        if( isInputWire && targetBlock.id !== block.id ||
            !isInputWire && sourceBlock.id !== block.id )
        {
            throw new Error(`Invalid flowBlock graph state; blockIds don't match for input wire of ` +
                `blockId: ${block.id} (block: ${block}) because ${isInputWire ? 'input' : 'output'} wire has ` +
                `targetBlockID: ${targetBlock.id} (block: ${targetBlock})`);
        }

        return {
            'out': sourceBlock.id,
            'outPort': sourcePortName,
            'in': targetBlock.id,
            'inPort': targetPortName,
            'grouping': {
                'type': 'mintdata.model.flow.grouping.ShuffleGrouping',
                'values': []
            }
        };
    }

}
